import { html } from 'lit-element';
import { nothing } from 'lit-html';
import { spreadProps } from '@open-wc/lit-helpers';
import { ifNotNull } from '../../utils/directives';
import {
  KatalComponent,
  KatLitElement,
  booleanConverter,
  coerceToArray,
  coerceBooleanClassProperties,
  nextUniqueId,
} from '../../shared/base';
import getString from './get-string';

/**
 * @component {kat-radiobutton-group} KatalRadioButtonGroup A form control that allows a user to select one of several options.
 * @guideline Do All input fields should have a label.
 * @event change This event is fired whenever the selected radio button changes.
 * @event focus This event is fired whenever a radio button in the group is focused and the last focused element was outside the group.
 * @event blur This event is fired whenever focus leaves any of the radio buttons in the group to an element outside of the group.
 * @status Production
 * @theme flo
 * @example Basic {"name": "group1", "value": "one", "label":"Radio Button Group", "options": [{"value": "one", "label": "First option"}, {"value": "two", "label": "Second option"},{"value": "three", "label": "Third option"}]}
 * @example Disabled {"name": "group1", "disabled": true, "value": "one", "label":"Radio Button Group", "options": [{"value": "one", "label": "First option"}, {"value": "two", "label": "Second option"}, {"value": "three", "label": "Third option"}]}
 * @example Complex {"name": "group1", "value": "one", "label":"Radio Button Group", "constraint-label": "Longer description of the group.", "error-label": "Please select a option", "info-tooltip": "Additional information to display in a tooltip", "options": [{"value": "one", "label": "First option", "constraintLabel": "secondary label on first option"}, {"value": "two", "label": "Second option (disabled)", "disabled": true}, {"value": "three", "label": "Third option"}]}
 */
class KatalRadioButtonGroup extends KatLitElement {
  _groupLabelId = nextUniqueId();
  _groupConstraintLabelId = nextUniqueId();

  static get properties() {
    return {
      /**
       * @required
       * @classprop {string} name The name used to group radio buttons.
       */
      name: { type: String, reflect: true },

      /**
       * @classprop {string} value If set then the child button with the same value will be selected.
       */
      value: { type: String, reflect: true },

      /**
       * @classprop {json} options An array of options used to populate the group with radio buttons. Each item in the array is a object and the properties are assigned to the radio buttons as properties. e.g. [{"label":"First","value":"1", "constraintLabel": "secondary label"}, {"label":"Second","value":"2"}]
       * @jsonExample - {"options": "[{"label":"First","value":"1"}, {"label":"Second","value":"2"}]"}
       * @required
       */
      options: { type: Array },

      /**
       * @classprop {string} label The title of the whole group.
       */
      label: { type: String, reflect: true },

      /**
       * @classprop {string} constraint-label A message explaining the purpose of the group.
       */
      constraintLabel: {
        type: String,
        attribute: 'constraint-label',
        reflect: true,
      },

      /**
       * @classprop {string} error-label The error message to display for the group.
       */
      errorLabel: { type: String, attribute: 'error-label', reflect: true },

      /**
       * @classprop {boolean} disabled If set will disable all radio buttons in the group.
       */
      disabled: { type: Boolean, converter: booleanConverter, reflect: true },

      /**
       * @classprop {string} info-tooltip A message used to provide additional information. This will be placed inside a tooltip on an icon next to the label.
       */
      infoTooltip: { type: String, attribute: 'info-tooltip', reflect: true },

      /**
       * @param {boolean} katal-1-2-spacing-fixes A special compatibility flag that when set removes extra margins around this component and sets its height to
       * the correct default. Note that this will become the default behavior in the next major release of Katal. Take a look at our service bulletin page for
       * more details.
       */
      katal12SpacingFixes: {
        type: Boolean,
        converter: booleanConverter,
        attribute: 'katal-1-2-spacing-fixes',
        reflect: true,
      },
    };
  }

  constructor() {
    super();

    this._options = [];
    this._lastFocusedRadio = null;
    this._lastBlurredRadio = null;
  }

  // disable shadow-dom
  createRenderRoot() {
    return this;
  }

  get options() {
    return this._options;
  }

  /**
   * @deprecated
   * This logic will be removed in 1.3
   */
  set options(val) {
    const oldVal = this.options;
    this._options = coerceToArray(this, 'options', val);
    this.requestUpdate('options', oldVal);
  }

  handleRadioChange(e) {
    const radio = e.target;
    this.value = radio.value;

    this.dispatchCustomEvent('change', { value: this.value });
  }

  handleRadioFocus(e) {
    e.stopPropagation();
    this._lastFocusedRadio = e.target;

    if (this._lastBlurredRadio === null) {
      this.dispatchCustomEvent('focus');
    }
  }

  handleRadioBlur(e) {
    e.stopPropagation();
    this._lastBlurredRadio = e.target;
    this._lastFocusedRadio = null;

    // The focus handler above should be executed before this setTimeout callback
    setTimeout(() => {
      if (this._lastFocusedRadio === null) {
        this.dispatchCustomEvent('blur');
      }
      this._lastBlurredRadio = null;
    }, 0);
  }

  /**
   * Programmatically focus the group. Focuses the first radio button.
   * @katalmethod
   */
  focus() {
    const first = this.getElementsByTagName('kat-radiobutton')[0];
    if (first) {
      first.focus();
    }
  }

  /**
   * Programmatically blur any focused radio buttons.
   * @katalmethod
   */
  blur() {
    Array.from(this.getElementsByTagName('kat-radiobutton')).forEach(button => {
      button.blur();
    });
  }

  render() {
    const radioButtons = this.options.map(option => {
      // eslint-disable-next-line no-unused-vars
      const { name, disabled = false, ...otherProps } = option;

      return html`
        <kat-radiobutton
          .name=${this.name}
          ?checked=${option.value === this.value}
          ?disabled=${this.disabled || disabled}
          @focus=${this.handleRadioFocus}
          @blur=${this.handleRadioBlur}
          ...=${spreadProps(otherProps)}
        ></kat-radiobutton>
      `;
    });

    const ariaTrigger = getString(
      'katal-radiobutton-group-aria-tooltip-trigger'
    );

    const tooltip = this.infoTooltip
      ? html`
          <kat-tooltip
            trigger-icon="help_outline"
            size="small"
            position="top"
            label=${this.infoTooltip}
            kat-aria-trigger=${ariaTrigger}
          ></kat-tooltip>
        `
      : nothing;

    const error = this.errorLabel
      ? html`
          <kat-statusindicator
            label=${this.errorLabel}
            variant="error"
          ></kat-statusindicator>
        `
      : nothing;

    const constraintLabel = this.constraintLabel
      ? html`
          <div
            class="kat-radiobutton-group-constraint-label"
            id=${this._groupConstraintLabelId}
          >
            ${this.constraintLabel}
          </div>
        `
      : nothing;

    return html`
      <div class="kat-radiobutton-group-label">
        <span id=${this._groupLabelId}>${this.label}</span>
        ${tooltip}
      </div>
      ${constraintLabel} ${error}
      <div
        role="radiogroup"
        aria-disabled=${ifNotNull(this.disabled ? 'true' : undefined)}
        aria-labelledby=${this._groupLabelId}
        aria-describedby=${ifNotNull(
          this.constraintLabel ? this._groupConstraintLabelId : undefined
        )}
        @change=${this.handleRadioChange}
      >
        ${radioButtons}
      </div>
    `;
  }
}
coerceBooleanClassProperties(KatalRadioButtonGroup);
KatalComponent.register('kat-radiobutton-group', KatalRadioButtonGroup);
